import { AvertissementDroitsImage } from '../../composants/AvertissementDroitsImage.js';
import { useMediasDeRattachement } from '../../api/medias.js';
import { Button, Checkbox, Dialog, Input, Select, Textarea } from '@artosera/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOeuvres, useOeuvre, type OeuvrePresentee } from '../../api/oeuvres.js';
import { useApercuMedia } from '../../api/medias.js';
import { useContacts } from '../../api/contacts.js';
import { useCreerDossierOeuvres, useOuvrirDossierPdf } from '../../api/dossiersOeuvres.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { estApiError } from '../../api/errors.js';
import { galerieAModule } from '../../auth/sujet.js';
import { ObjetCartel, proprietesCartel } from '../../composants/ObjetCartel.js';
import { VignetteOeuvre } from '../../composants/VignetteOeuvre.js';
import { FeuillePiece } from '../../composants/FeuillePiece.js';
import { Field } from '../Field.js';
import { useShellContext } from '../shell/context.js';
import styles from './DossierOeuvresDialog.module.css';
function OeuvreSelectionnee({
  g,
  id,
  lang,
  connue,
}: {
  g: string;
  id: string;
  lang: 'fr' | 'en';
  connue?: OeuvrePresentee;
}) {
  const fiche = useOeuvre(g, connue ? null : id);
  const oeuvre = connue ?? fiche.data?.oeuvre;
  const media = useApercuMedia(g, oeuvre?.mediaPrincipalId ?? null);
  const medias = useMediasDeRattachement(g, 'artwork', id);
  return (
    <>
      <VignetteOeuvre url={media.data ?? null} taille="sm" />
      <AvertissementDroitsImage
        medias={(medias.data ?? []).filter((m) => m.id === oeuvre?.mediaPrincipalId)}
        usages={['impression', 'presse']}
      />
      <div className={styles.cartel}>
        {oeuvre ? <ObjetCartel {...proprietesCartel(oeuvre, lang)} /> : id}
      </div>
    </>
  );
}
export function DossierOeuvresDialog({
  open,
  onOpenChange,
  oeuvreIds,
  contactInitial,
  titreInitial = '',
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  oeuvreIds: readonly string[];
  contactInitial?: { id: string; nom: string };
  titreInitial?: string;
}) {
  const { galerieSlug: g, galerie } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const tr = (key: string, values?: Record<string, unknown>) =>
    t(`regie:dossiersOeuvres.${key}`, values);
  const [titre, setTitre] = useState(titreInitial),
    [message, setMessage] = useState(''),
    [langue, setLangue] = useState<'fr' | 'en'>(i18n.language === 'en' ? 'en' : 'fr');
  const [ids, setIds] = useState<string[]>([...new Set(oeuvreIds)]),
    [contact, setContact] = useState(contactInitial?.id ?? ''),
    [qContact, setQContact] = useState(''),
    [q, setQ] = useState('');
  const [avecPrix, setAvecPrix] = useState(false),
    [avecProvenance, setAvecProvenance] = useState(false),
    [erreur, setErreur] = useState<string | null>(null);
  // Après une panne du rendu, reprendre le PDF du dossier déjà créé.
  const [cree, setCree] = useState<string | null>(null);
  const creer = useCreerDossierOeuvres(g),
    pdf = useOuvrirDossierPdf(g);
  const contacts = useContacts(
    g,
    { q: qContact, parPage: 8 },
    { enabled: open && !contactInitial },
  );
  const oeuvres = useOeuvres(g, { q, parPage: 30, vignettes: true }, { enabled: open });
  const provenance = galerieAModule(galerie, 'editions-provenance');
  function deplacer(index: number, delta: number) {
    setIds((a) => {
      const b = [...a];
      [b[index], b[index + delta]] = [b[index + delta]!, b[index]!];
      return b;
    });
  }
  async function preparer() {
    setErreur(null);
    try {
      let id = cree;
      if (!id) {
        const r = await creer.mutateAsync({
          titre: titre.trim(),
          oeuvres: ids,
          ...(contact ? { contactId: contact } : {}),
          ...(message.trim() ? { message: message.trim() } : {}),
          langue,
          avecPrix,
          avecProvenance: provenance && avecProvenance,
        });
        id = r.dossier.id;
        setCree(id);
      }
      const document = await pdf.mutateAsync(id);
      ouvrirUrlSignee(document.url);
      onOpenChange(false);
    } catch (e) {
      setErreur(estApiError(e) ? e.message : t('commun:erreurReseau'));
    }
  }
  const connus = oeuvres.data?.oeuvres ?? [];
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={tr('titre')}
      closeLabel={t('commun:actions.fermer')}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('commun:actions.annuler')}
          </Button>
          <Button
            loading={creer.isPending || pdf.isPending}
            disabled={!titre.trim() || ids.length === 0 || ids.length > 30}
            onClick={() => void preparer()}
          >
            {cree ? tr('reprendrePdf') : tr('preparer')}
          </Button>
        </>
      }
    >
      <div className={styles.formulaire} data-ui="works-portfolio-dialog">
        <Field label={tr('titreChamp')} htmlFor="d-titre">
          <Input
            id="d-titre"
            maxLength={200}
            value={titre}
            disabled={cree !== null}
            onChange={(e) => setTitre(e.target.value)}
          />
        </Field>
        {contactInitial ? (
          <p>
            {tr('destinataire')} : {contactInitial.nom}
          </p>
        ) : (
          <Field label={tr('contactRecherche')} htmlFor="d-contact-q">
            <Input
              id="d-contact-q"
              value={qContact}
              disabled={cree !== null}
              onChange={(e) => setQContact(e.target.value)}
            />
            <Select
              aria-label={tr('destinataire')}
              value={contact}
              disabled={cree !== null}
              onValueChange={setContact}
              options={[
                { value: '', label: tr('sansContact') },
                ...(contacts.data?.contacts ?? []).map((c) => ({
                  value: c.id,
                  label: c.nomAffiche,
                })),
              ]}
            />
          </Field>
        )}
        <Field label={tr('message')} htmlFor="d-message">
          <Textarea
            id="d-message"
            rows={3}
            maxLength={4000}
            value={message}
            disabled={cree !== null}
            onChange={(e) => setMessage(e.target.value)}
          />
        </Field>
        <Field label={tr('langue')} htmlFor="d-langue">
          <Select
            id="d-langue"
            value={langue}
            disabled={cree !== null}
            onValueChange={(v) => setLangue(v === 'en' ? 'en' : 'fr')}
            options={[
              { value: 'fr', label: 'Français' },
              { value: 'en', label: 'English' },
            ]}
          />
        </Field>
        <Checkbox
          id="d-prix"
          label={tr('avecPrix')}
          checked={avecPrix}
          disabled={cree !== null}
          onCheckedChange={(v) => setAvecPrix(v === true)}
        />
        {provenance && (
          <Checkbox
            id="d-provenance"
            label={tr('avecProvenance')}
            checked={avecProvenance}
            disabled={cree !== null}
            onCheckedChange={(v) => setAvecProvenance(v === true)}
          />
        )}
        <Field label={tr('rechercheOeuvres')} htmlFor="d-oeuvres-q">
          <Input
            id="d-oeuvres-q"
            value={q}
            disabled={cree !== null}
            onChange={(e) => setQ(e.target.value)}
          />
        </Field>
        {q.trim() && (
          <ul className={styles.selection}>
            {connus
              .filter((o) => !ids.includes(o.id))
              .slice(0, 8)
              .map((o) => (
                <li className={styles.ligne} key={o.id}>
                  <ObjetCartel {...proprietesCartel(o, langue)} />
                  <Button
                    variant="secondary"
                    disabled={ids.length >= 30 || cree !== null}
                    onClick={() => setIds((a) => [...a, o.id])}
                  >
                    {tr('ajouter', { nom: o.titre })}
                  </Button>
                </li>
              ))}
          </ul>
        )}
        <FeuillePiece
          emetteur={galerie.nom ?? g}
          nature={tr('titre')}
          destinataire={{
            legende: tr('destinataire'),
            nom:
              contactInitial?.nom ??
              contacts.data?.contacts.find((c) => c.id === contact)?.nomAffiche ??
              tr('sansContact'),
          }}
          numero={titre || tr('apercu')}
          brouillon
          meta={tr('nombre', { count: ids.length })}
        >
          <ol className={styles.selection}>
            {ids.map((id, index) => (
              <li className={styles.ligne} key={id}>
                <OeuvreSelectionnee
                  g={g}
                  id={id}
                  lang={langue}
                  {...(connus.find((o) => o.id === id)
                    ? { connue: connus.find((o) => o.id === id)! }
                    : {})}
                />
                <div className={styles.gestes}>
                  <Button
                    variant="ghost"
                    disabled={index === 0 || cree !== null}
                    aria-label={tr('monter', { nom: connus.find((o) => o.id === id)?.titre ?? id })}
                    onClick={() => deplacer(index, -1)}
                  >
                    ↑
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={index === ids.length - 1 || cree !== null}
                    aria-label={tr('descendre', {
                      nom: connus.find((o) => o.id === id)?.titre ?? id,
                    })}
                    onClick={() => deplacer(index, 1)}
                  >
                    ↓
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={cree !== null}
                    onClick={() => setIds((a) => a.filter((i) => i !== id))}
                  >
                    {tr('retirer')}
                  </Button>
                </div>
              </li>
            ))}
          </ol>
        </FeuillePiece>
        <p className={styles.meta}>{tr('limite')}</p>
        {erreur && (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        )}
      </div>
    </Dialog>
  );
}
